import { resolveComponent, defineComponent, h } from 'vue';

interface VNode {
  type: any;
  props: object;
  children: VNodeChild[] | object | null;
  text?: string;
  key?: string;
}
type VNodeChild = any;
type NodeMethod = (node: HTMLElement) => VNode;
type NodeMethods = { [key: string]: NodeMethod };

// 文字列をキャピタライズするユーティリティ関数
const capitalize = (str: string): string => {
  return str
    .split('-')
    .map((s) => {
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    })
    .join('');
};
// ノードのメソッド名を取得するユーティリティ関数
const getNodeMethodNames = (node: HTMLElement): string => {
  let name;
  if (node.nodeType === Node.TEXT_NODE) {
    name = node.nodeName.slice(1);
  } else if (node.nodeType === Node.ELEMENT_NODE) {
    name = node.tagName;
  } else {
    console.log('Not implemented nodeType: ' + node.nodeType);
  }
  return 'node' + capitalize(name);
};

export default defineComponent({
  props: {
    content: { type: String, required: true }
  },
  setup(props) {
    const vmethods: NodeMethods = {
      // テキストノードをVNodeに変換するメソッド
      nodeText(node: HTMLElement): VNode {
        const vNode: VNode = {
          type: null,
          props: {},
          children: null,
          text: node.textContent || "",
        };
        return vNode;
      },
      // 一般的なノードをVNodeに変換するメソッド
      nodeGeneric(node: HTMLElement): VNode {
        const _props: any = {};
        for (let i = 0; i < node.attributes.length; i++) {
          const attr = node.attributes[i];
          _props[attr.name] = attr.value;
        }
        const vNode: VNode = {
          type: node.tagName.toLowerCase(),
          props: _props,
          children: null,
        };
        return vNode;
      },
      // 画像ノードをVNodeに変換するメソッド
      nodeImage(node: HTMLElement): VNode {
        const ZoomableImageComponent = resolveComponent('zoomable-image') as any;
        const newVNode: VNode = {
          type: ZoomableImageComponent,
          key: `${node.tagName.toLowerCase()}-${Math.random()}`,
          props: {
            src: node.getAttribute('src') || "",
            alt: node.getAttribute('alt') || "",
            width: node.getAttribute('width') || "",
            height: node.getAttribute('height') || "",
            imgClass: node.getAttribute('class') || "",
          },
          children: null,
        };
        return newVNode;
      }
    }
    // ノードを再帰的に処理するメソッド
    const processNodeTree = (node: HTMLElement): any => {
      let vNode: VNode;
      // ノード名に対応するメソッドが存在するか確認
      const nodeName = getNodeMethodNames(node);
      if (nodeName in vmethods) {
        vNode = (vmethods[nodeName] as NodeMethod)(node);
      } else {
        // 対応するメソッドが存在しない場合は一般的なメソッドを使用
        vNode = (vmethods.nodeGeneric as NodeMethod)(node);
      }
      // mathjaxのタグに対する処理をスキップする条件分岐
      if (vNode.type !== 'mjx-container') {
        // 子ノードを処理し、childrenプロパティに設定
        const children: VNodeChild[] = Array.from(node.childNodes).map((childNode) => {
          return processNodeTree(childNode as HTMLElement);
        });
        if (children.length > 0) {
          vNode.children = children;
        }
      }
      // 画像タグの場合、画像ノードを処理
      if (vNode.type === "img") {
        vNode = (vmethods.nodeImage as NodeMethod)(node);
      }
      // vNodeがnullの場合のログ出力
      if (!vNode) {
        console.log('WARN: vNode is null', { node, vNode });
      }
      // vNodeのtextが存在するかで分岐し、処理を実行
      if (vNode.text) {
        return vNode.text;
      }
      // mjx-containerの場合、innerHTMLを設定して返す
      else if (vNode.type === 'mjx-container') {
        return h(vNode.type, { innerHTML: node.innerHTML })
      }
      else {
        return h(vNode.type, vNode.props, vNode.children);
      }
    }
    return () => {
      // contentが存在しない場合は空のdivを返す
      if (!props.content) {
        return h('div');
      }
      // contentを解析し、paragraph要素をdiv要素に置換
      let parser = new DOMParser();
      let doc = parser.parseFromString(props.content, 'text/html');
      let paragraphs = doc.querySelectorAll('p');
      paragraphs.forEach(function (paragraph) {
        let div = document.createElement('div');
        div.innerHTML = paragraph.innerHTML;
        paragraph.parentNode.replaceChild(div, paragraph);
      });
      // DOM構造を再構築し、最終的なVNodeを返す
      const outer = document.createElement('div');
      outer.innerHTML = doc.body.innerHTML;
      return processNodeTree(outer);
    }
  }
});